import * as Types from '@/types';
import { makeStableReturn, sortByRank } from '@/services/helpers/helpers-without-framework';
import {
    SentryErrorEmitterService,
    AuthContextService,
} from '@/services';
import { UiRights } from '@/configuration';

type APISingleResponse<T> = Types.UI.APISingleResponse<T>;
type BankAccount = Types.BillingApi.BankAccount;
type OrchestrationJob = Types.ViewTypes.OrchestrationJob;
type PaymentMethod = Types.BillingApi.PaymentMethod;

export type GenericInjectedRequests = {
    createBankAccount: (iban: string, accountHolder: string, bic: string) => Promise<APISingleResponse<BankAccount>>,
    checkPaymentMethodsAvailability: () => Promise<PaymentMethod[]>,
    getBankAccount: () => Promise<BankAccount[]>,
    refreshDeposit: () => void,
}

export interface SepaData {
    iban: string,
    accountHolder: string,
    bic?: string,
    bankName?: string,
}

export enum OrderItemStatus {
    PENDING = 'PENDING',
    SUCCESSFUL = 'SUCCESSFUL',
    SUPPORT = 'SUPPORT',
    BLANK = 'BLANK',
    NODOMAIN = 'NODOMAIN',
}

export type Loading = {
    state: 'loading';
};

export type Waiting = {
    state: 'waiting';
};

export type Loaded<T> = {
    state: 'loaded';
    data: T;
};
export type MaybeLoading<T> = Loading | Waiting | Loaded<T>;


export class OrderItemDisplayLine {
    public readonly id: string;
    public readonly objectType: string;
    public readonly status: OrderItemStatus;

    constructor(
        id: string,
        objectType: string,
        status: OrderItemStatus,
    ) {
        this.id = id;
        this.objectType = objectType;
        this.status = status;
    }
}

export abstract class GenericSetup {

    public foundOrderId: 'loading' | 'found' | 'notfound' = 'loading';
    public previousJobsForRefreshDeposit: OrchestrationJob[] = [];

    // fetched async from API
    public jobs?: OrchestrationJob[];
    private bankAccount: Types.BillingApi.BankAccount;
    public sepaAllowed = true;

    // running async processes
    public paymentMethodIsUpdating = false;

    protected abstract requests: GenericInjectedRequests;

    protected abstract jobIsDone: (job: OrchestrationJob) => void;

    public updateOrchestrationJobs(jobs: OrchestrationJob[]) {
        this.jobs = jobs;
        // Load objects, if status is successful
        if (this.foundOrderId === 'loading') {
            this.foundOrderId = this.jobs.length > 0 ? 'found' : 'notfound';
            if (this.foundOrderId === 'notfound') {
                SentryErrorEmitterService.sendSentryReport(
                    'Setup of given orderId failed',
                    null,
                    { component: 'Setup' },
                    SentryErrorEmitterService.messageTypes.Info,
                );
            }
        }

        for (const job of jobs) {
            if (job.status === 'successful' || job.status === 'done') {
                this.jobIsDone(job);
            }
        }
        this.triggerRefreshDepositIfNeeded(jobs);

    }

    protected triggerRefreshDepositIfNeeded(jobs: OrchestrationJob[]) {
        if (this.previousJobsForRefreshDeposit.length === 0) {
            this.previousJobsForRefreshDeposit = jobs;
        }
        for (let i = 0; i < jobs.length; i++) {
            if (jobs[i].status != this.previousJobsForRefreshDeposit[i].status) {
                this.requests.refreshDeposit();
                break;
            }
        }
        this.previousJobsForRefreshDeposit = jobs;
    }

    public loadInitialData = () => {
        this.checkSepaAllowedAsPaymentMethod();
        this.getBankAccount();
    }

    protected abstract orderItemDisplayLinesBeforeJobIsLoaded: () => OrderItemDisplayLine[];
    protected abstract postprocessOrderItemDisplayLines: (lines: OrderItemDisplayLine[]) => void;

    orderItemDisplayLines = makeStableReturn(() => {
        if (!Array.isArray(this.jobs)) {
            return this.orderItemDisplayLinesBeforeJobIsLoaded();
        }
        const objectTypeOrder = ['Bundle', 'Webspace', 'Domain', 'Zone', 'VHost'];
        const jobs = [...this.jobs];
        sortByRank(jobs, objectTypeOrder);
        const lines: OrderItemDisplayLine[] = [];
        for (const job of jobs) {
            let status: OrderItemStatus;
            switch (job.status) {
                case 'done':
                case 'successful':
                    status = OrderItemStatus.SUCCESSFUL;
                    break;
                case 'failed':
                case 'canceled':
                case 'support':
                    status = OrderItemStatus.SUPPORT;
                    break;
                default:
                    status = OrderItemStatus.PENDING;
            }
            lines.push(new OrderItemDisplayLine(job.objectType, job.objectType, status));
        }
        this.postprocessOrderItemDisplayLines(lines);
        return lines;
    });

    get orderCompleted(): boolean {
        if (this.jobs === undefined) {
            return false;
        }
        for (const job of this.jobs) {
            if (job.status !== 'successful') {
                return false;
            }
        }
        return true;
    }

    get showFoundOrSearchingPanel(): boolean {
        return this.foundOrderId !== 'notfound';
    }

    public saveSepaPaymentMethod: (
        sepaData: SepaData,
    ) => Promise<void> = async (
        sepaData: SepaData,
    ) => {
        // delete BIC information, if bic is not required
        if ((/^DE/i.test(sepaData.iban))) {
            delete sepaData.bic;
        }
        this.paymentMethodIsUpdating = true;
        const bic = sepaData.bic && sepaData.bic.length > 0 ? sepaData.bic : undefined;
        let apiResponse;

        apiResponse = await this.requests.createBankAccount(
            sepaData.iban,
            sepaData.accountHolder,
            bic,
        );
        this.getBankAccount();
        this.paymentMethodIsUpdating = false;
    }

    public checkSepaAllowedAsPaymentMethod = (): void => {
        this.requests.checkPaymentMethodsAvailability().then(
            (paymentMethodAvailability: {paymentMethod: string; available: boolean}[]) => {
                let allowed = true;
                paymentMethodAvailability.forEach((element) => {if (element.paymentMethod === 'directDebit') allowed = element.available});
                this.sepaAllowed = allowed;
            }
        );
    }

    public getBankAccount = () => {
        if (AuthContextService.isAdminUser) {
            this.requests.getBankAccount().then(
                (response: BankAccount[]) => {
                    if (response.length === 0) {
                        return;
                    }
                    this.bankAccount = response[0];
                }
            );
        }
    }

    get sepaStatus(): string {
        if (this.bankAccount === undefined)
            return '';
        return this.bankAccount.status;
    }

    public get showSepaPanel(): boolean {
        return AuthContextService.isAdminUser
            && this.sepaAllowed
            && AuthContextService.isGrantedAll(
                [
                    UiRights.BIL_LIST_DOCUMENTS,
                    UiRights.ACC_ACCOUNT_USER_LIST,
                    UiRights.BIL_GET_DEPOSIT,
                ]
            );
    }

}
